import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {CustomerEmailService} from '../../api/service/customer.email.service';
import {escape as lshEscape} from 'lodash';
import {Observable, Scheduler} from 'rxjs';
import {EmailTypes, MailingConfig, MailingRequest, MailingResponse, MailingStatus} from '../../api/entity';

export class EmailSendStatus {
    status: MailingStatus;
    errorMessage;

    constructor(status: MailingStatus = MailingStatus.NOT_SEND, errorMessage = '') {
        this.status = status;
        this.errorMessage = errorMessage;
    }
}

@Component({
    selector: 'tfl-email-send',
    template: require('./email.send.template.html'),
    providers: [CustomerEmailService]
})
export class EmailSendComponent implements OnInit, OnDestroy {
    @Input() profileId: number;
    @Input() userId: number;
    @Input() emailType: EmailTypes = EmailTypes.UNKNOWN;
    @Input() emailData = '';

    @Input('success-icon-style') successIconStyle = 'fa-check-circle';
    @Input('success-style') successStyle = 'success';

    @Input('progress-icon-style') progressIconStyle = 'fa-repeat';
    @Input('progress-style') progressStyle = 'alert-dark';

    @Input('failure-icon-style') failureIconStyle = 'fa-exclamation-triangle';
    @Input('failure-style') failureStyle = 'warning';

    @Input('disable-icon-style') disableIconStyle = 'fa-exclamation-triangle';
    @Input('disable-style') disableStyle = 'warning';

    @Input('not-send-icon-style') notSendIconStyle = 'fa-minus-circle';
    @Input('not-send-style') notSendStyle = 'warning';

    MailingStatusType = MailingStatus;

    sendEmailStatus: MailingStatus = MailingStatus.NOT_SEND;

    @Output('on-send-email-status') sendEmailStatusEvent = new EventEmitter<EmailSendStatus>();

    errorMessage = '';
    errorMessageDetails = '';

    @Input() spinnerSize = 20;

    private statusIcons: Map<MailingStatus, string> = new Map<MailingStatus, string>();
    private statusStyles: Map<MailingStatus, string> = new Map<MailingStatus, string>();

    private mailingConfigActions: Map<EmailTypes, () => Observable<MailingConfig>> = new Map<EmailTypes, () => Observable<MailingConfig>>();
    private mailingSendActions: Map<EmailTypes, (MailingRequest) => Observable<MailingResponse>> = new Map<EmailTypes, () => Observable<MailingResponse>>();

    constructor(private _customerEmailService: CustomerEmailService) {
        let defaultConfigAction = () => this._customerEmailService.getMailingConfigByProfile(this.profileId);
        this.mailingConfigActions.set(EmailTypes.REGISTRATION_LINK, defaultConfigAction);
        this.mailingConfigActions.set(EmailTypes.CONFIRMATION_CODE, defaultConfigAction);
        this.mailingConfigActions.set(EmailTypes.RESET_PASSWORD_LINK, () => this._customerEmailService.getMailingConfigByUser(this.userId));

        let defaultSendAction = (request) => this._customerEmailService.sendApproveCustomerEmail(this.profileId, request);
        this.mailingSendActions.set(EmailTypes.REGISTRATION_LINK, defaultSendAction);
        this.mailingSendActions.set(EmailTypes.CONFIRMATION_CODE, defaultSendAction);
        this.mailingSendActions.set(EmailTypes.RESET_PASSWORD_LINK, (request) => this._customerEmailService.sendResetPasswordCustomerEmail(this.userId, request));
    }

    ngOnInit(): void {
        this.statusIcons.set(MailingStatus.NOT_SEND, this.notSendIconStyle);
        this.statusIcons.set(MailingStatus.SENDING, this.progressIconStyle);
        this.statusIcons.set(MailingStatus.SUCCESS, this.successIconStyle);
        this.statusIcons.set(MailingStatus.FAILURE, this.failureIconStyle);
        this.statusIcons.set(MailingStatus.DISABLE, this.disableIconStyle);

        this.statusStyles.set(MailingStatus.NOT_SEND, this.notSendStyle);
        this.statusStyles.set(MailingStatus.SENDING, this.progressStyle);
        this.statusStyles.set(MailingStatus.SUCCESS, this.successStyle);
        this.statusStyles.set(MailingStatus.FAILURE, this.failureStyle);
        this.statusStyles.set(MailingStatus.DISABLE, this.disableStyle);
    }

    ngOnDestroy(): void {
    }

    public tryToSendEmail(): void {
        if (this.mailingConfigActions.has(this.emailType)) {
            let getMailConfigAction = this.mailingConfigActions.get(this.emailType);

            let result = getMailConfigAction().flatMap(mailingConfig => {
                if (mailingConfig.mailingEnable) {
                    return this.sendEmail();
                } else {
                    return Observable.from([new EmailSendStatus(MailingStatus.DISABLE)], Scheduler.async);
                }
            });

            result.subscribe((emailSendStatus) => {
                this.sendEmailStatus = emailSendStatus.status;
                this.errorMessage = emailSendStatus.errorMessage;
                this.sendEmailStatusEvent.emit(emailSendStatus);
            }, (error) => {
                let errorResult = this.handleMailError(error);
                this.sendEmailStatus = errorResult.status;
                this.sendEmailStatusEvent.emit(new EmailSendStatus(this.sendEmailStatus));
                this.errorMessage = errorResult.errorMessage;
                this.errorMessageDetails = errorResult.errorMessageDetails;
            }, () => {
                if (this.sendEmailStatus === MailingStatus.SENDING) {
                    this.sendEmailStatus = MailingStatus.NOT_SEND;
                }
            });
        } else {
            console.warn(`Config mailing action was not found for [${this.emailType}] email type`);
        }
    }

    private sendEmail(): Observable<EmailSendStatus> {

        let sendEmailAction = () => {
            let mailingRequest = new MailingRequest(this.emailType, this.emailData);
            if (this.mailingSendActions.has(this.emailType)) {
                let sendEmailActionItem = this.mailingSendActions.get(this.emailType);
                return sendEmailActionItem(mailingRequest);
            } else {
                return Observable.empty<any>();
            }
        };

        this.sendEmailStatus = MailingStatus.SENDING;

        return sendEmailAction()
            .map((emailSendResponse) => {
                let status = emailSendResponse.success ? MailingStatus.SUCCESS : MailingStatus.FAILURE;
                let errorMsg = '';
                if (status === MailingStatus.FAILURE) {
                    errorMsg = lshEscape(emailSendResponse.errorMessage);
                }

                return new EmailSendStatus(status, errorMsg);
            });
    }

    public handleMailError(error: any): any {
        let msg = '';
        let msgDetails = '';

        let hasNestedError = error.messageArgs
            && error.messageArgs.success !== undefined;

        if (hasNestedError) {
            return this.handleMailError(error.messageArgs);
        } else {
            if (error.hasOwnProperty('success')) {
                msg = error.error_message;
                if (Array.isArray(error.errors) && error.errors.length) {
                    msgDetails = error.errors.join();
                }
            } else {
                if (error.hasOwnProperty('message')) {
                    msg = error.message;
                }
            }

            return {
                status: MailingStatus.FAILURE,
                errorMessage: lshEscape(msg),
                errorMessageDetails: lshEscape(msgDetails)
            }
        }
    }

    public iconClassByStatus(): string {
        let s: string = this.statusIcons.get(this.sendEmailStatus);
        return `fa ${s} fa-2x`;
    }

    private emailSendStatusStyle(): string {
        return this.statusStyles.get(this.sendEmailStatus);
    }

    public static isAllEmailComponentsWithStatus(mailingComponents: EmailSendComponent[], status: MailingStatus) {
        return mailingComponents.map((component) => component.sendEmailStatus)
            .every(s => s === status)
    }

    public static isAnyEmailComponentsWithStatus(mailingComponents: EmailSendComponent[], status: MailingStatus) {
        return mailingComponents.map((component) => component.sendEmailStatus)
            .some(s => s === status)
    }

    private isSendingComplete() {
        return this.sendEmailStatus !== MailingStatus.NOT_SEND && this.sendEmailStatus !== MailingStatus.SENDING;
    }
}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./~/angular-router-loader/src!./src/dashboard/app/modules/customers/shared/components/emails/email.send.component.ts